import { supabase } from './supabase';

// ── Voter token ──────────────────────────────────────────────────────────────
// A random UUID generated once per browser. No PII — just prevents double-votes
// from the same device without collecting any identity.

export function getVoterToken() {
  let token = localStorage.getItem('voter_token');
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem('voter_token', token);
  }
  return token;
}

// ── Cast vote ────────────────────────────────────────────────────────────────
// Returns { ok: true } or { ok: false, duplicate: true } or { ok: false, error }

export async function castVote(stateId, district, partyId) {
  const voterToken = getVoterToken();
  const { error } = await supabase.from('votes').insert({
    state_id:    stateId,
    district,
    party_id:    partyId,
    voter_token: voterToken,
  });

  if (!error) return { ok: true };

  console.error('[castVote] Supabase error:', error);

  // Postgres unique violation — already voted from this browser
  if (error.code === '23505') return { ok: false, duplicate: true };

  return { ok: false, error: error.message };
}

// ── Check if this browser already voted for a state ──────────────────────────
// Returns the vote object or null.

export async function getExistingVote(stateId) {
  const voterToken = getVoterToken();

  // Check localStorage cache first (instant)
  const cached = localStorage.getItem(`${stateId}:user_vote`);
  if (cached) {
    try { return JSON.parse(cached); } catch (_) {}
  }

  // Fall back to DB (handles cleared localStorage)
  const { data } = await supabase
    .from('votes')
    .select('district, party_id, created_at')
    .eq('state_id', stateId)
    .eq('voter_token', voterToken)
    .maybeSingle();

  if (data) {
    const vote = { district: data.district, party: data.party_id, ts: new Date(data.created_at).getTime() };
    localStorage.setItem(`${stateId}:user_vote`, JSON.stringify(vote));
    return vote;
  }
  return null;
}

// ── Save vote to localStorage cache after successful DB insert ───────────────
export function cacheVote(stateId, vote) {
  localStorage.setItem(`${stateId}:user_vote`, JSON.stringify(vote));
}

// ── Fetch aggregated vote counts for a state ─────────────────────────────────
// Returns { [district]: { [partyId]: count } } or {} if results not published.

export async function fetchCounts(stateId) {
  const { data, error } = await supabase
    .from('votes')
    .select('district, party_id')
    .eq('state_id', stateId);

  if (error || !data) return {};

  const counts = {};
  for (const row of data) {
    if (!counts[row.district]) counts[row.district] = {};
    counts[row.district][row.party_id] = (counts[row.district][row.party_id] || 0) + 1;
  }
  return counts;
}

// ── Check if results are published for a state ───────────────────────────────
export async function isResultsPublished(stateId) {
  const { data } = await supabase
    .from('config')
    .select('value')
    .eq('key', `${stateId}_results_published`)
    .maybeSingle();

  return data?.value === 'true';
}
